import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Loader2, ExternalLink } from 'lucide-react';
import { useCatalogSearch } from '../hooks/useCatalogSearch';
import { WishlistPickerMenu } from '../components/WishlistPickerMenu';

export default function SearchResults() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  const [inputValue, setInputValue] = useState(initialQuery);
  const [query, setQuery] = useState(initialQuery);
  const { data, isFetching } = useCatalogSearch(query, 50);
  const results = data?.results;
  const expansion = data?.expansion;
  const hybrid = data?.hybrid;

  // Sync URL param → query on navigation
  useEffect(() => {
    const q = searchParams.get('q') || '';
    setInputValue(q);
    setQuery(q);
  }, [searchParams]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = inputValue.trim();
    if (trimmed.length >= 2) {
      setQuery(trimmed);
      setSearchParams({ q: trimmed });
    }
  }

  function formatPrice(price: number | null, currency: string | null) {
    if (price == null) return null;
    const sym = currency === 'USD' || !currency ? '$' : currency + ' ';
    return `${sym}${price.toFixed(2)}`;
  }

  function relevanceBadgeClass(fraction: number | undefined) {
    if (fraction == null) return 'bg-muted text-muted-foreground';
    if (fraction >= 0.999) return 'bg-emerald-600 text-white';
    if (fraction >= 0.66) return 'bg-emerald-500/90 text-white';
    if (fraction >= 0.4) return 'bg-amber-500/90 text-white';
    return 'bg-muted-foreground/70 text-white';
  }

  return (
    <div className="space-y-6">
      {/* Search bar */}
      <form onSubmit={handleSubmit} className="relative">
        <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder="Search products by name, brand, or retailer..."
          className="flex h-12 w-full rounded-lg border border-input bg-background pl-12 pr-12 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          autoFocus
        />
        {isFetching && (
          <Loader2 className="absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 animate-spin text-muted-foreground" />
        )}
      </form>

      {/* Results count + expansion info */}
      {query.length >= 2 && !isFetching && results && (
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground">
            {results.length === 0
              ? `No results for "${query}"`
              : `${results.length} result${results.length !== 1 ? 's' : ''} for "${query}"`}
          </p>
          {/* Expander banner — internal-only AWL UI. Shows what the LLM
              query expander did, if anything. */}
          {expansion && expansion.added.length > 0 && (
            <p className="text-xs text-muted-foreground/80 font-mono">
              expanded with: {expansion.added.join(', ')}
              {expansion.cached ? ' (cached)' : ''}
            </p>
          )}
          {expansion?.error && (
            <p className="text-xs text-amber-600 font-mono">
              expander failed ({expansion.error}) — searched literally
            </p>
          )}
          {/* Vector hybrid indicator. */}
          {hybrid && (
            <p className="text-xs text-muted-foreground/80 font-mono">
              hybrid text+vector search · ranking by combined score
            </p>
          )}
        </div>
      )}

      {/* Results grid */}
      {results && results.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {results.map((result) => {
            return (
              <div
                key={result.catalog_product_id}
                className="rounded-lg border border-border bg-card overflow-hidden hover:shadow-md transition-shadow"
              >
                {/* Image */}
                <div className="relative aspect-square bg-muted">
                  {result.image_url ? (
                    <img
                      src={result.image_url}
                      alt={result.name || ''}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center">
                      <span className="text-muted-foreground text-xs">No image</span>
                    </div>
                  )}
                  {/* Relevance badge — internal-only AWL UI, surfaces the
                      ranking signal so we can see why a result placed where
                      it did. Hover for the score breakdown. In hybrid mode
                      we show the hybrid_score; otherwise the text-only
                      match_fraction. */}
                  {result.hybrid_score != null ? (
                    <span
                      className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-mono font-semibold tabular-nums shadow ${relevanceBadgeClass(result.hybrid_score)}`}
                      title={`hybrid ${result.hybrid_score.toFixed(2)} = ${result.match_fraction?.toFixed(2)} text + ${result.vector_similarity?.toFixed(2)} vector · text ${result.search_score}/${result.q_total} · name ${result.name_score}/${result.q_total}`}
                    >
                      {Math.round(result.hybrid_score * 100)}%
                    </span>
                  ) : result.match_fraction != null && (
                    <span
                      className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-mono font-semibold tabular-nums shadow ${relevanceBadgeClass(result.match_fraction)}`}
                      title={`search_text ${result.search_score}/${result.q_total} · name ${result.name_score}/${result.q_total} · fraction ${result.match_fraction.toFixed(2)}`}
                    >
                      {Math.round(result.match_fraction * 100)}%
                    </span>
                  )}
                </div>

                {/* Details */}
                <div className="p-3 space-y-2">
                  <h3 className="text-sm font-medium text-card-foreground line-clamp-2 leading-tight">
                    {result.name || 'Untitled'}
                  </h3>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground truncate">
                      {result.retailer}
                    </span>
                    {result.current_best_price != null && (
                      <span className="text-sm font-semibold text-foreground">
                        {formatPrice(result.current_best_price, result.currency)}
                      </span>
                    )}
                  </div>

                  {result.q_total != null && (
                    <div className="text-[10px] text-muted-foreground/80 font-mono tabular-nums">
                      text {result.search_score}/{result.q_total} · name {result.name_score}/{result.q_total}
                      {result.vector_similarity != null && (
                        <> · vec {result.vector_similarity.toFixed(2)}</>
                      )}
                    </div>
                  )}

                  <div className="flex gap-2 items-start">
                    <WishlistPickerMenu result={result} />
                    <a
                      href={result.normalized_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center rounded-md border border-input bg-background px-2 py-1.5 text-xs hover:bg-accent hover:text-accent-foreground"
                    >
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Empty state */}
      {query.length >= 2 && !isFetching && results && results.length === 0 && (
        <div className="text-center py-12">
          <Search className="mx-auto h-12 w-12 text-muted-foreground/50" />
          <h3 className="mt-4 text-lg font-medium text-foreground">No products found</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            Try a different search term, or add a product URL directly.
          </p>
        </div>
      )}
    </div>
  );
}
